import { useState, useEffect, useId, useRef } from 'react';
import { X, Plus, Upload, ChevronDown, Save, Loader2, AlertCircle, Info } from 'lucide-react';
import { getDefaultCategories, getCategoryConfig } from '../utils/categoryConfig';
import { getApiBase, getAuthHeaders, setAuthToken } from '../utils/apiClient';
import { auth } from '../firebaseClient';
import { formatMinutes, hasTime, toMinutes } from '../utils/recipeTime';
import { TimeSelect } from './TimeSelect';
import { MealTypeSelect } from './MealTypeSelect';

// Human-friendly label for where an imported recipe came from, used in the review banner.
// Returns '' when we can't name the source, so the copy falls back to a generic sentence.
function describeImportSource(source) {
  const raw = String(source || '').trim();
  if (!raw) return '';
  if (/^text import$/i.test(raw)) return 'the text you pasted';
  try {
    const host = new URL(raw).hostname.replace(/^www\./, '');
    if (host) return host;
  } catch {
    // Not a URL (e.g. a hand-typed source) — fall through to the generic wording.
  }
  return '';
}

// `isImported` distinguishes "review this freshly imported draft" from "edit a recipe you
// already saved". Both arrive via existingRecipe, so the flag is the only way to tell them apart.
export function ManualRecipeForm({ isOpen, onClose, onSave, existingRecipe, isImported = false, userId = 'user-default' }) {
  const getInitialRecipeState = () => {
    if (existingRecipe) {
      return {
        id: existingRecipe.id,
        name: existingRecipe.name || '',
        servings: existingRecipe.servings || 4,
        // Canonical minutes (or null). Imports may hand us "1 hr 30 mins" or an ISO
        // duration, so parse rather than assume this is already a number.
        prepTime: toMinutes(existingRecipe.prepTime),
        cookTime: toMinutes(existingRecipe.cookTime),
        // Single figure from a source that didn't split prep/cook. Superseded as soon as
        // either dropdown is set (see setTime).
        totalTime: toMinutes(existingRecipe.totalTime),
        source: existingRecipe.source || existingRecipe.sourceUrl || '',
        ingredients: existingRecipe.ingredients?.map((ing) => 
          typeof ing === 'string' ? { quantity: '', name: ing } : { quantity: ing.quantity, name: ing.name, category: ing.category }
        ) || [{ quantity: '', name: '' }],
        method: existingRecipe.method || [''],
        photo: existingRecipe.photo || existingRecipe.image || '',
        imagePath: existingRecipe.imagePath || '',
        imageThumb: existingRecipe.imageThumb || existingRecipe.thumbnail || existingRecipe.thumbUrl || '',
        imageThumbPath: existingRecipe.imageThumbPath || existingRecipe.thumbnailPath || existingRecipe.thumbPath || '',
        tags: existingRecipe.tags || [],
        mealType: existingRecipe.mealType || '',
        nutrition: existingRecipe.nutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 },
      };
    }
    return {
      name: '',
      servings: 4,
      prepTime: null,
      cookTime: null,
      totalTime: null,
      source: '',
      ingredients: [{ quantity: '', name: '' }],
      method: [''],
      photo: '',
      imagePath: '',
      imageThumb: '',
      imageThumbPath: '',
      tags: [],
      mealType: '',
      nutrition: { calories: '', protein: '', carbs: '', fat: '' },
    };
  };

  const [recipe, setRecipe] = useState(getInitialRecipeState());
  const [tagsText, setTagsText] = useState(() => (getInitialRecipeState().tags || []).join(', '));
  const [customCategories, setCustomCategories] = useState([]);
  const [showCategoryInput, setShowCategoryInput] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const summaryRef = useRef(null);
  const defaultCategories = getDefaultCategories();
  const importSourceLabel = isImported ? describeImportSource(recipe.source) : '';

  // Setting either half of the breakdown supersedes an imported single total, so drop it —
  // otherwise the form would show a total that no longer drives anything.
  const setTime = (field) => (minutes) => setRecipe((prev) => {
    const next = { ...prev, [field]: minutes };
    if (hasTime(next.prepTime) && hasTime(next.cookTime)) next.totalTime = null;
    return next;
  });

  const clampServings = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return 1;
    return Math.min(20, Math.max(1, n));
  };

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateRecipe = () => {
    const nextErrors = {};
    const normalizedIngredients = [];
    const normalizedMethod = [];

    const name = String(recipe.name || '').trim();
    if (!name) nextErrors.name = 'Recipe name is required.';

    recipe.ingredients.forEach((ingredient, index) => {
      const quantity = String(ingredient.quantity || '').trim();
      const ingredientName = String(ingredient.name || '').trim();
      const category = String(ingredient.category || '').trim();

      if (!quantity && !ingredientName && !category) return;

      if (!ingredientName) {
        nextErrors[`ingredient-${index}`] = 'Ingredient name is required.';
        return;
      }

      normalizedIngredients.push({
        quantity,
        name: ingredientName,
        category: category || undefined,
      });
    });

    recipe.method.forEach((step) => {
      const normalizedStep = String(step || '').trim();
      if (normalizedStep) normalizedMethod.push(normalizedStep);
    });

    if (!normalizedIngredients.length) nextErrors.ingredients = 'Add at least one ingredient.';
    if (!normalizedMethod.length) nextErrors.method = 'Add at least one method step.';

    // Meal type is mandatory and has no default, so an untouched form must fail here rather
    // than silently saving as lunch. Drives meal-plan generation, so a wrong value matters.
    const mealType = String(recipe.mealType || '').trim().toLowerCase();
    if (!mealType) nextErrors.mealType = 'Choose which meal type/s this recipe belongs to';

    return {
      isValid: Object.keys(nextErrors).length === 0,
      nextErrors,
      normalizedRecipe: {
        ...recipe,
        name,
        mealType,
        servings: clampServings(recipe.servings),
        prepTime: recipe.prepTime,
        cookTime: recipe.cookTime,
        totalTime: recipe.totalTime,
        source: String(recipe.source || '').trim(),
        ingredients: normalizedIngredients,
        method: normalizedMethod,
      },
    };
  };

  // Imports with no serving size default to 4. Say so at the Servings field (neutral info, not an
  // error; doesn't block saving). role="status" only when the note appears after the field is
  // already showing (form was open), so it's announced once; on open it's read with the field.
  const servingsNoteId = useId();
  const [showServingsNote, setShowServingsNote] = useState(false);
  const [announceServingsNote, setAnnounceServingsNote] = useState(false);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const appearsLate = wasOpenRef.current && isOpen;
    wasOpenRef.current = isOpen;
    const show = !!(isOpen && existingRecipe?.servingsDefaulted);
    setShowServingsNote(show);
    setAnnounceServingsNote(show && appearsLate);
  }, [isOpen, existingRecipe]);

  // Update recipe state when existingRecipe changes
  useEffect(() => {
    if (isOpen) {
      setRecipe(getInitialRecipeState());
      setTagsText((getInitialRecipeState().tags || []).join(', '));
      setUploadError('');
      setIsUploading(false);
      
      // Extract custom categories from existing recipe
      if (existingRecipe?.ingredients) {
        const customs = existingRecipe.ingredients
          .map((ing) => ing.category)
          .filter((cat) => cat && !defaultCategories.includes(cat))
          .filter((cat, index, self) => self.indexOf(cat) === index);
        setCustomCategories(customs);
      }
      
      // Auto-resize all method textareas after render
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + 'px';
        });
      }, 0);
    }
  // We intentionally only depend on isOpen and existingRecipe.id here to
  // avoid re-running when other recipe fields change during editing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingRecipe?.id]);

  if (!isOpen) return null;

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
    if (!allowed.has(file.type)) {
      const message = 'Use JPEG, PNG, or WEBP images.';
      setUploadError(message);
      event.target.value = '';
      return;
    }

    if (file.size > 12 * 1024 * 1024) {
      const message = 'Image is too large. Max size is 12MB.';
      setUploadError(message);
      event.target.value = '';
      return;
    }

    const previousPhoto = recipe.photo;
    const previousPath = recipe.imagePath || '';
    const previousThumb = recipe.imageThumb || '';
    const previousThumbPath = recipe.imageThumbPath || '';
    const previewUrl = URL.createObjectURL(file);
    setRecipe((prev) => ({ ...prev, photo: previewUrl }));
    setUploadError('');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', userId || 'user-default');

      // Ensure we have a fresh ID token; fallback to auth.currentUser if apiClient header is empty
      const baseHeaders = getAuthHeaders();
      if (!baseHeaders.Authorization && auth?.currentUser) {
        const freshToken = await auth.currentUser.getIdToken(true);
        setAuthToken(freshToken);
        baseHeaders.Authorization = `Bearer ${freshToken}`;
      }

      const resp = await fetch(`${getApiBase()}/uploads/recipe-image`, {
        method: 'POST',
        headers: baseHeaders,
        body: formData,
      });

      if (!resp.ok) {
        let detail = '';
        try {
          const json = await resp.json();
          detail = json?.error || json?.details || '';
        } catch (err) {
          detail = err?.message || '';
        }
        throw new Error(detail || 'Upload failed');
      }

      const payload = await resp.json();
      setRecipe((prev) => ({
        ...prev,
        photo: payload.url,
        imagePath: payload.path,
        imageThumb: payload.thumbUrl || prev.imageThumb,
        imageThumbPath: payload.thumbPath || prev.imageThumbPath,
      }));
    } catch (err) {
      const message = err?.message || 'Could not upload image';
      setRecipe((prev) => ({
        ...prev,
        photo: previousPhoto,
        imagePath: previousPath,
        imageThumb: previousThumb,
        imageThumbPath: previousThumbPath,
      }));
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddIngredient = () => {
    clearError('ingredients');
    setRecipe({
      ...recipe,
      ingredients: [...recipe.ingredients, { quantity: '', name: '' }],
    });
  };

  const handleRemoveIngredient = (index) => {
    if (recipe.ingredients.length === 1) return;
    clearError('ingredients');
    clearError(`ingredient-${index}`);
    setRecipe({
      ...recipe,
      ingredients: recipe.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleIngredientChange = (index, value, field) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index][field] = value;
    setRecipe({ ...recipe, ingredients: newIngredients });
    if (field === 'name') {
      clearError(`ingredient-${index}`);
    }
  };

  const handleCategoryChange = (index, category) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index].category = category;
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const handleAddCustomCategory = (ingredientIndex) => {
    if (newCategoryName.trim() && !defaultCategories.includes(newCategoryName.trim()) && !customCategories.includes(newCategoryName.trim())) {
      const newCategory = newCategoryName.trim();
      setCustomCategories([...customCategories, newCategory]);
      handleCategoryChange(ingredientIndex, newCategory);
      setNewCategoryName('');
      setShowCategoryInput(null);
    }
  };

  const handleAddStep = () => {
    clearError('method');
    setRecipe({
      ...recipe,
      method: [...recipe.method, ''],
    });
  };

  const handleRemoveStep = (index) => {
    if (recipe.method.length === 1) return;
    clearError('method');
    setRecipe({
      ...recipe,
      method: recipe.method.filter((_, i) => i !== index),
    });
  };

  const handleStepChange = (index, value) => {
    const newMethod = [...recipe.method];
    newMethod[index] = value;
    setRecipe({ ...recipe, method: newMethod });
    clearError('method');
  };

  const handleNutritionChange = (field, value) => {
    setRecipe((prev) => ({
      ...prev,
      nutrition: {
        ...(prev.nutrition || {}),
        [field]: value,
      },
    }));
  };

  const handleTagsChange = (value) => {
    setTagsText(value);
    const tagsArray = value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    setRecipe((prev) => ({
      ...prev,
      tags: tagsArray,
    }));
  };

  const handleSave = () => {
    const validation = validateRecipe();
    if (!validation.isValid) {
      setErrors(validation.nextErrors);
      focusSummary();
      return;
    }

    setErrors({});
    onSave(validation.normalizedRecipe);
    handleClose();
  };

  const handleClose = () => {
    setRecipe({
      name: '',
      servings: 4,
      prepTime: null,
      cookTime: null,
      totalTime: null,
      source: '',
      ingredients: [{ quantity: '', name: '' }],
      method: [''],
      photo: '',
      imagePath: '',
      imageThumb: '',
      imageThumbPath: '',
      tags: [],
      mealType: '',
      nutrition: { calories: '', protein: '', carbs: '', fat: '' },
    });
    setUploadError('');
    setIsUploading(false);
    setErrors({});
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={handleClose}
      />
      
      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 bg-card text-foreground rounded-t-3xl z-50 max-h-[95vh] overflow-hidden">
        <div className="max-w-md md:max-w-2xl w-full mx-auto flex flex-col max-h-[95vh]">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-muted rounded-full" />
          </div>

          {/* Header */}
          <div className="px-6 pb-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl">
                  {isImported ? 'Review imported recipe' : existingRecipe ? 'Edit Recipe' : 'Add Recipe'}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <form className="space-y-6 pt-6" noValidate>
              {/* Sage tint via an arbitrary hex, not bg-primary/10: the semantic colour classes
                  are hand-written in index.css and have no opacity variants, so "/10" matches
                  nothing. --primary is #AFBB8B in both light and dark, so this is theme-safe. */}
              {isImported && (
                <div className="rounded-2xl border border-[#AFBB8B]/40 bg-[#AFBB8B]/15 px-4 py-3">
                  {/* WCAG 2.2 AA: text-muted-foreground (#717182) is only 4.35:1 on this tint in
                      light mode, so the copy uses text-foreground (17.99:1 light / 14.93:1 dark).
                      The icon uses primary-foreground because text-primary (#AFBB8B) is 1.85:1
                      here — near invisible. Both are theme-aware, so no dark: variants needed. */}
                  <div className="flex items-start gap-2">
                    <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-foreground" />
                    <p className="text-sm text-foreground">
                      {`We’ve pulled this in${importSourceLabel ? ` from ${importSourceLabel}` : ''}.`}
                      <br></br>
                      {`Some sites format their recipe data a little differently from what’s shown on their page.`} 
                      <br></br>
                      {`Allergen and dietary tags come from the site directly.`}
                     <br></br>
                      {'Double check quantities and details before saving your recipe.'} 
                      
                    </p>
                  </div>
                </div>
              )}

              {Object.keys(errors).length > 0 && (
                <div
                  ref={summaryRef}
                  tabIndex={-1}
                  role="alert"
                  aria-live="assertive"
                  className="rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 outline-none focus:ring-2 focus:ring-[#B85A5A]"
                >
                  <div className="flex items-start gap-2 text-[#B85A5A]">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium">Please fix the highlighted fields below.</p>
                      <ul className="list-disc pl-5">
                        {errors.name && <li>{errors.name}</li>}
                        {errors.mealType && <li>{errors.mealType}</li>}
                        {errors.ingredients && <li>{errors.ingredients}</li>}
                        {errors.method && <li>{errors.method}</li>}
                        {Object.entries(errors)
                          .filter(([key]) => key.startsWith('ingredient-'))
                          .map(([key, message]) => (
                            <li key={key}>{message}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Upload */}
              <div>
                <label className="block text-foreground mb-2">
                  Recipe Photo (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="block cursor-pointer"
                >
                  {recipe.photo ? (
                    <div className="relative">
                      <img
                        src={recipe.photo}
                        alt="Recipe"
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <span className="text-white text-sm">{isUploading ? 'Uploading...' : 'Change Photo'}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-muted rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-[var(--border)] hover:border-primary transition-colors">
                      <Upload className="w-10 h-10 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Upload a photo</span>
                    </div>
                  )}
                </label>
                {uploadError && (
                  <p className="mt-2 text-sm text-destructive">{uploadError}</p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  JPEG/PNG/WEBP, up to 12MB. We resize and optimize automatically.
                </p>
              </div>

              {/* Recipe Name */}
              <div>
                <label htmlFor="recipeName" className="block text-foreground mb-2">
                  Recipe Name *
                </label>
                {errors.name && (
                  <div id="manual-recipe-name-error" className="mb-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                    <span>{errors.name}</span>
                  </div>
                )}
                <input
                  type="text"
                  id="recipeName"
                  value={recipe.name}
                  onChange={(e) => {
                    setRecipe({ ...recipe, name: e.target.value });
                    clearError('name');
                  }}
                  placeholder="e.g., Creamy Tuscan Chicken"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'manual-recipe-name-error' : undefined}
                  className={`w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-foreground placeholder-muted-foreground ${errors.name ? 'ring-2 ring-[#B85A5A]' : ''}`}
                  required
                />
                
              </div>

              {/* Servings */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="servings" className="block text-foreground mb-2 text-sm">
                    Servings
                  </label>
                  <select
                    id="servings"
                    value={recipe.servings}
                    aria-describedby={showServingsNote ? servingsNoteId : undefined}
                    onChange={(e) => {
                      setRecipe((prev) => ({ ...prev, servings: clampServings(e.target.value) }));
                      setShowServingsNote(false); // they've checked it
                    }}
                    className="w-full px-3 py-3 bg-input rounded-xl border border-[var(--border)] focus:border-primary focus:ring-2 focus:ring-primary outline-none text-foreground"
                    style={{ WebkitAppearance: 'menulist', appearance: 'auto' }}
                  >
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showServingsNote && (
                <p
                  id={servingsNoteId}
                  role={announceServingsNote ? 'status' : undefined}
                  className="-mt-1 flex items-start gap-2 text-sm text-foreground"
                >
                  <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#5F6B3F]" aria-hidden="true" />
                  <span>We couldn’t find a serving size for this recipe, so we’ve set it to 4. Please check it so your shopping list quantities are right.</span>
                </p>
              )}

              {/* Prep & Cook Time — stacked on mobile, paired once the drawer widens at md.
                  Structured dropdowns rather than free text, so there is no unit to mis-parse. */}
              <div className="grid gap-4 md:grid-cols-2">
                <TimeSelect
                  label="Prep Time"
                  idPrefix="prepTime"
                  minutes={recipe.prepTime}
                  onChange={setTime('prepTime')}
                />
                <TimeSelect
                  label="Cook Time"
                  idPrefix="cookTime"
                  minutes={recipe.cookTime}
                  onChange={setTime('cookTime')}
                />
              </div>

              {/* The total drives filtering but has no dropdown of its own, so say so
                  rather than leaving it as state the user can't see. */}
              {hasTime(recipe.totalTime) && (
                <p className="-mt-2 text-sm text-muted-foreground">
                  Imported total time: {formatMinutes(recipe.totalTime)}. Setting both prep and cook time will replace it.
                </p>
              )}

              {/* Suppressed when a total is known — the recipe is already filterable, so the
                  prompt would be untrue. */}
              {!hasTime(recipe.totalTime) && (!hasTime(recipe.prepTime) || !hasTime(recipe.cookTime)) && (
                <p className="-mt-2 text-sm text-muted-foreground">
                  Add prep + cook time to make this recipe filterable by time
                </p>
              )}

              <MealTypeSelect
                value={recipe.mealType}
                onChange={(key) => {
                  setRecipe((prev) => ({ ...prev, mealType: key }));
                  clearError('mealType');
                }}
                error={errors.mealType}
                idPrefix="mealType"
              />

              {/* Tags */}
              <div>
                <label htmlFor="tags" className="block text-foreground mb-2">
                  Tags (optional)
                </label>
                <input
                  type="text"
                  id="tags"
                  value={tagsText}
                  onChange={(e) => handleTagsChange(e.target.value)}
                  placeholder="e.g., quick, family-friendly, vegetarian"
                  className="w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Separate tags with commas
                </p>
              </div>

              {/* Nutrition */}
              <div>
                <label className="block text-foreground mb-2">
                  Nutrition per serving (optional)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label htmlFor="calories" className="block text-muted-foreground mb-1 text-xs">
                      Calories (kcal)
                    </label>
                    <input
                      type="number"
                      id="calories"
                      min="0"
                      value={recipe.nutrition?.calories ?? ''}
                      onChange={(e) => handleNutritionChange('calories', e.target.value)}
                      className="w-full px-3 py-2 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground"
                    />
                  </div>
                  <div>
                    <label htmlFor="protein" className="block text-muted-foreground mb-1 text-xs">
                      Protein (g)
                    </label>
                    <input
                      type="number"
                      id="protein"
                      min="0"
                      value={recipe.nutrition?.protein ?? ''}
                      onChange={(e) => handleNutritionChange('protein', e.target.value)}
                      className="w-full px-3 py-2 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground"
                    />
                  </div>
                  <div>
                    <label htmlFor="carbs" className="block text-muted-foreground mb-1 text-xs">
                      Carbs (g)
                    </label>
                    <input
                      type="number"
                      id="carbs"
                      min="0"
                      value={recipe.nutrition?.carbs ?? ''}
                      onChange={(e) => handleNutritionChange('carbs', e.target.value)}
                      className="w-full px-3 py-2 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground"
                    />
                  </div>
                  <div>
                    <label htmlFor="fat" className="block text-muted-foreground mb-1 text-xs">
                      Fat (g)
                    </label>
                    <input
                      type="number"
                      id="fat"
                      min="0"
                      value={recipe.nutrition?.fat ?? ''}
                      onChange={(e) => handleNutritionChange('fat', e.target.value)}
                      className="w-full px-3 py-2 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <label className="block text-foreground mb-2">
                  Ingredients *
                </label>
                {errors.ingredients && (
                  <div className="mb-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                    <span>{errors.ingredients}</span>
                  </div>
                )}
                <div className="space-y-3">
                  {recipe.ingredients.map((ingredient, index) => {
                    const categoryConfig = ingredient.category 
                      ? getCategoryConfig(
                          ingredient.category, 
                          customCategories.indexOf(ingredient.category) >= 0 
                            ? customCategories.indexOf(ingredient.category) 
                            : undefined
                        )
                      : null;
                    const CategoryIcon = categoryConfig?.icon;

                    return (
                      <div key={index} className="space-y-2 p-3 bg-muted rounded-xl">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={ingredient.quantity}
                            onChange={(e) => handleIngredientChange(index, e.target.value, 'quantity')}
                            placeholder="Qty"
                            className="w-24 px-3 py-2 bg-card rounded-lg border border-[var(--border)] focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground"
                          />
                          <input
                            type="text"
                            value={ingredient.name}
                            onChange={(e) => handleIngredientChange(index, e.target.value, 'name')}
                            placeholder="Ingredient name"
                            aria-invalid={!!errors[`ingredient-${index}`]}
                            aria-describedby={errors[`ingredient-${index}`] ? `manual-ingredient-${index}-error` : undefined}
                            className={`flex-1 px-3 py-2 bg-card rounded-lg border border-[var(--border)] focus:ring-2 focus:ring-primary outline-none text-sm text-foreground placeholder-muted-foreground ${errors[`ingredient-${index}`] ? 'ring-2 ring-[#B85A5A]' : ''}`}
                          />
                          {recipe.ingredients.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveIngredient(index)}
                              className="px-2 text-destructive hover:bg-muted rounded-lg transition-colors text-xl"
                              aria-label="Remove ingredient"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {errors[`ingredient-${index}`] && (
                          <div id={`manual-ingredient-${index}-error`} className="flex items-center gap-1.5 text-sm text-[#B85A5A]">
                            <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                            <span>{errors[`ingredient-${index}`]}</span>
                          </div>
                        )}
                        
                        {/* Category Selection */}
                        {showCategoryInput === index ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder="Enter custom category"
                              className="flex-1 px-3 py-2 bg-white rounded-lg border-0 focus:ring-2 focus:ring-[#7B9EC6] outline-none text-sm"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCustomCategory(index);
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleAddCustomCategory(index)}
                              className="px-3 py-2 bg-[#7B9EC6] text-white rounded-lg text-sm hover:bg-[#5B7E9E] transition-colors"
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCategoryInput(null);
                                setNewCategoryName('');
                              }}
                              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <select
                              value={ingredient.category || ''}
                              onChange={(e) => {
                                if (e.target.value === '__custom__') {
                                  setShowCategoryInput(index);
                                  setNewCategoryName('');
                                } else {
                                  handleCategoryChange(index, e.target.value);
                                }
                              }}
                              className={`w-full py-2 bg-white rounded-lg border-0 focus:ring-2 focus:ring-[#7B9EC6] outline-none text-sm appearance-none pr-8 ${
                                ingredient.category ? 'pl-8' : 'pl-3'
                              }`}
                            >
                              <option value="">No category</option>
                              {defaultCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                              {customCategories.length > 0 && <option disabled>───</option>}
                              {customCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                              <option value="__custom__">+ Add custom category</option>
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            {CategoryIcon && (
                              <CategoryIcon 
                                className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" 
                                style={{ color: categoryConfig?.color }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={handleAddIngredient}
                    className="flex items-center gap-1 text-sm link-ingredient"
                  >
                    <Plus className="w-4 h-4" />
                    Add Ingredient
                  </button>
                </div>
              </div>

              {/* Method */}
              <div>
                <label className="block text-foreground mb-2">
                  Method *
                </label>
                {errors.method && (
                  <div className="mb-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                    <span>{errors.method}</span>
                  </div>
                )}
                <div className="space-y-2">
                  {recipe.method.map((step, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--accent-light)] flex items-center justify-center text-sm text-[var(--accent-foreground)] mt-3">
                        {index + 1}
                      </div>
                      <textarea
                        value={step}
                        onChange={(e) => {
                          handleStepChange(index, e.target.value);
                          // Auto-resize textarea
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onInput={(e) => {
                          // Also handle on input for better responsiveness
                          const target = e.target;
                          target.style.height = 'auto';
                          target.style.height = target.scrollHeight + 'px';
                        }}
                        placeholder={`Step ${index + 1}`}
                        className="flex-1 px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-[var(--primary)] outline-none resize-y overflow-auto min-h-[96px]"
                        style={{ height: 'auto' }}
                      />
                      {recipe.method.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveStep(index)}
                          className="px-3 text-[var(--accent-foreground)] hover:bg-[var(--accent-light)] rounded-xl transition-colors text-xl"
                          aria-label="Remove step"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddStep}
                    className="flex items-center gap-1 text-sm link-ingredient ml-8"
                  >
                    <Plus className="w-4 h-4" />
                    Add Step
                  </button>
                </div>
              </div>

              {/* Source */}
              <div>
                <label htmlFor="source" className="block text-foreground mb-2">
                  Source (Optional)
                </label>
                <input
                  type="text"
                  id="source"
                  value={recipe.source}
                  onChange={(e) => setRecipe({ ...recipe, source: e.target.value })}
                  placeholder="e.g., Food Network"
                  className="w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-[var(--primary)] outline-none"
                />
              </div>
            </form>
          </div>

          {/* Bottom Actions */}
          <div className="px-6 py-4 bg-card">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] bg-card hover:bg-[var(--primary-light)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isUploading}
                className={`flex-1 py-3 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] transition-colors ${isUploading ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[var(--primary-hover)]'}`}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isUploading ? 'Uploading photo...' : 'Save Recipe'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
